"use client";

import { useEffect, useRef, useState } from "react";
import Icon from "@hackclub/icons";

/** Footer control on a "Curated Sparks" card: a quiet "Delete" that opens a
 *  small confirmation popup before calling DELETE /api/spark-factory/drafts/{id}. */
export function CuratedDeleteAction({
  appId,
  name,
  onDeleted,
  onError,
}: {
  appId: string;
  name: string;
  onDeleted: () => void;
  onError: (message: string) => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  function close() {
    if (!deleting) setOpen(false);
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      const res = await fetch(`/api/spark-factory/drafts/${encodeURIComponent(appId)}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ app_id: appId }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error || "We couldn't delete that spark. Please try again.");
      }
      setOpen(false);
      onDeleted();
    } catch (error) {
      setOpen(false);
      onError((error as Error).message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="sbx-del"
        aria-label={`Delete ${name}`}
        onClick={() => setOpen(true)}
      >
        <Icon glyph="delete" size={18} />
        Delete
      </button>

      <dialog
        ref={dialogRef}
        className="sbx-confirm"
        aria-labelledby={`sbx-confirm-${appId}`}
        onCancel={(e) => {
          e.preventDefault();
          close();
        }}
      >
        <div className="sbx-confirm__icon">
          <Icon glyph="delete" size={24} />
        </div>
        <h2 id={`sbx-confirm-${appId}`} className="sbx-confirm__title">
          Delete this spark?
        </h2>
        <p className="sbx-confirm__text">
          &ldquo;{name}&rdquo; will be permanently removed. This can&apos;t be undone.
        </p>
        <div className="sbx-confirm__actions">
          <button type="button" className="sbx-confirm__cancel" onClick={close} disabled={deleting}>
            Cancel
          </button>
          <button
            type="button"
            className="sbx-confirm__delete"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting && <span className="sbx-confirm__spinner" aria-hidden="true" />}
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </dialog>
    </>
  );
}
